import React, { useState, useEffect, useMemo } from 'react';
import { useReactFlow, useStore } from '@xyflow/react';
import { Select, InputNumber, Space, Flex } from 'antd';
import {
  MoveParams,
  RotateParams,
  ScaleParams,
  PlayAnimationParams,
  PlayAudioParams,
  DisplayTextParams,
  NoParams,
  IncrementCounterParams,
  CustomParams,
  PlayCutsceneParams,
  ShowModalParams,
  MoveMainCameraParams,
  RotateMainCameraParams,
  SwitchBetweenCamerasParams,
  SpawnNPCParams,
  PlayNPCActionParams,
} from './actionParams/index';
import { ActionType } from '@src/components/generated/graphql';
import {
  DEFAULT_PRESET_ADVANCED_SETTINGS,
  NODES_TYPES,
  NPCTypes,
  NPCAction,
  RequiredNPCAnimationsTypes,
} from '../../types';
import styles from '../../timeline-node-manager.module.css';
import type { EventActionType } from '@src/types/event-timeline';

const { Option } = Select;

interface ActionParamsProps {
  nodeId: string;
  selectableAssets: any[];
}

const DEFAULT_CUTSCENE_URL =
  'https://d23hwblhugcea5.cloudfront.net/Dragon/RPG/FightClub/FC_Interface/MediaFiles/game_intro.mp4';

const DEFAULT_PLAY_AUDIO_SOUND =
  'https://d23hwblhugcea5.cloudfront.net/studioAssets/QuestSFX/sfx_right.mp3';

const ActionParams: React.FC<ActionParamsProps> = ({
  nodeId,
  selectableAssets,
}: ActionParamsProps) => {
  const { setNodes } = useReactFlow();
  const node = useStore((state) => state.nodes.find((n) => n.id === nodeId));
  const action = node?.data ?? ({} as any);
  const [type, setType] = useState(action.type);
  const [params, setParams] = useState(action.params);
  const [delay, setDelay] = useState(action.delay);
  const [duration, setDuration] = useState(action.duration);

  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes);

  const targetInfo = useMemo(() => {
    const actionEdge = edges.find((edge) => edge.source === nodeId);
    if (!actionEdge) return null;

    const targetNode = nodes.find(
      (node) =>
        node.id === actionEdge.target && node.type === NODES_TYPES.TARGET
    );
    if (!targetNode) return null;

    return {
      objectId: targetNode.data.objectId as string,
      previewImage: targetNode.data.previewImage as string,
    };
  }, [nodeId, edges, nodes]);

  const onChangeNode = (key: string, value: string) => {
    setNodes((nodes) =>
      nodes.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              data: {
                ...n.data,
                [key]: value,
              },
            }
          : n
      )
    );
  };

  function onChangeParams(params: any) {
    setParams(params);
    onChangeNode('params', params);
  }

  const handleTypeChange = (value: EventActionType['type']) => {
    setType(value);
    onChangeNode('type', value);
    switch (value) {
      case ActionType.Move:
        onChangeParams({
          position: { x: 0, y: 0, z: 0 },
          easing: 'linear',
        });
        break;
      case ActionType.Rotate:
        onChangeParams({
          rotation: { x: 0, y: 0, z: 0 },
          easing: 'linear',
          duration: 10000,
        });
        break;
      case ActionType.Scale:
        onChangeParams({
          scale: { x: 1, y: 1, z: 1 },
          easing: 'linear',
        });
        break;
      case ActionType.PlayAnimation:
        onChangeParams({
          modelURL: '',
          loop: true,
          selectedAnimationName: '',
          animations: [],
        });
        break;
      case ActionType.PlayAudio:
        onChangeParams({
          audioURL: DEFAULT_PLAY_AUDIO_SOUND,
          volume: 1.0,
        });
        break;
      case ActionType.DisplayText:
        onChangeParams({
          text: 'New message',
          duration: 3000,
        });
        break;
      case ActionType.DisplayText:
        onChangeParams({
          title: 'Title',
          text: 'New message',
        });
        break;
      case ActionType.PlayCutscene:
        onChangeParams({
          videoUrl: DEFAULT_CUTSCENE_URL,
        });
        break;
      case ActionType.PlayNpcAction:
        onChangeParams({
          actionType: NPCAction.MOVE,
          actionParams: {
            pointToMove: { x: 5, y: 0, z: 5 },
          },
          isForced: false,
          targetNpcId: '',
        });
        break;
      case ActionType.ShowModal:
        onChangeParams({
          title: '',
          text: '',
          showModelPreview: false,
        });
        break;
      case ActionType.IncrementCounter:
        onChangeParams({
          counterId: '',
          value: 1,
        });
        break;
      case ActionType.SpawnNpc:
        onChangeParams({
          assetId: '',
          inventoryItemId: '',
          stopFollowAreaPosition: { x: 10, y: 0, z: 10 },
          animations: {
            [RequiredNPCAnimationsTypes.IDLE]: '',
            [RequiredNPCAnimationsTypes.WALK]: '',
          },
          onCompleteAction: null,
          npcType: NPCTypes.VILLAGER,
        });
        break;
      case ActionType.MoveMainCamera:
        onChangeParams({
          fromPosition: { x: 0, y: 0, z: 0 },
          toPosition: { x: 0, y: 0, z: 0 },
          enableLookAtTarget: true,
          presetSettings: DEFAULT_PRESET_ADVANCED_SETTINGS,
          easing: 'linear',
        });
        break;
      case ActionType.RotateMainCamera:
        onChangeParams({
          rotation: { x: 0, y: 0, z: 0 },
        });
        break;
      case ActionType.SwitchBetweenCameras:
        onChangeParams({
          position: { x: 0, y: 0, z: 0 },
          enableLookAtTarget: true,
        });
        break;
      case ActionType.Hide:
      case ActionType.Show:
      case ActionType.Custom:
        onChangeParams({});
        break;
    }
  };

  const renderParamsInputs = () => {
    switch (type) {
      case ActionType.Move:
        return (
          <MoveParams
            nodeId={node?.id || ''}
            params={params}
            setParams={onChangeParams}
          />
        );
      case ActionType.MoveMainCamera:
        return (
          <MoveMainCameraParams
            nodeId={node?.id || ''}
            params={params}
            setParams={onChangeParams}
          />
        );
      case ActionType.Rotate:
        return <RotateParams params={params} setParams={onChangeParams} />;

      case ActionType.Scale:
        return <ScaleParams params={params} setParams={onChangeParams} />;

      case ActionType.PlayAnimation:
        return (
          <PlayAnimationParams
            params={params}
            setParams={onChangeParams}
            data={action}
            target={targetInfo!}
            selectableAssets={selectableAssets}
          />
        );

      case ActionType.PlayAudio:
        return <PlayAudioParams params={params} setParams={onChangeParams} />;
      case ActionType.PlayNpcAction:
        return (
          <PlayNPCActionParams
            params={params}
            setParams={onChangeParams}
            nodeId={node?.id || ''}
            selectableAssets={selectableAssets}
          />
        );
      case ActionType.DisplayText:
        return <DisplayTextParams params={params} setParams={onChangeParams} />;
      case ActionType.ShowModal:
        return <ShowModalParams params={params} setParams={onChangeParams} />;
      case ActionType.IncrementCounter:
        return (
          <IncrementCounterParams params={params} setParams={onChangeParams} />
        );

      case ActionType.Hide:
      case ActionType.Show:
        return <NoParams params={params} setParams={onChangeParams} />;

      case ActionType.Custom:
        return <CustomParams params={params} setParams={onChangeParams} />;

      case ActionType.PlayCutscene:
        return (
          <PlayCutsceneParams params={params} setParams={onChangeParams} />
        );
      case ActionType.RotateMainCamera:
        return (
          <RotateMainCameraParams params={params} setParams={onChangeParams} />
        );
      case ActionType.SpawnNpc:
        return (
          <SpawnNPCParams
            nodeId={node?.id || ''}
            params={params}
            setParams={onChangeParams}
            selectableAssets={selectableAssets}
          />
        );
      case ActionType.SwitchBetweenCameras:
        return (
          <SwitchBetweenCamerasParams
            nodeId={node?.id || ''}
            params={params}
            setParams={onChangeParams}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    setParams(action.params);
    setType(action.type);
    setDelay(action.delay);
    setDuration(action.duration);
  }, [node?.id]);

  return (
    <>
      <div className={styles.actionParamsContainer}>
        <Flex gap={8} vertical style={{ width: '100%' }}>
          <span className={styles.label}>Type:</span>
          <Select
            value={type}
            onChange={handleTypeChange}
            style={{ width: '100%' }}
          >
            <Option value={ActionType.MoveMainCamera}>Move Main Camera</Option>
            <Option value={ActionType.Move}>Move</Option>
            <Option value={ActionType.PlayAnimation}>Play Animation</Option>
            <Option value={ActionType.PlayCutscene}>Play Cutscene</Option>
            <Option value={ActionType.PlayNpcAction}>Play NPC Action</Option>
            <Option value={ActionType.PlayAudio}>Play Audio</Option>
            <Option value={ActionType.RotateMainCamera}>
              Rotate Main Camera
            </Option>
            <Option value={ActionType.Rotate}>Rotate</Option>
            <Option value={ActionType.SwitchBetweenCameras}>
              Switch Camera
            </Option>
            <Option value={ActionType.ShowModal}>Show Modal</Option>
            <Option value={ActionType.SpawnNpc}>Spawn NPC</Option>
          </Select>
        </Flex>
      </div>
      {renderParamsInputs()}
      {(type === ActionType.Move ||
        type === ActionType.SwitchBetweenCameras ||
        type === ActionType.RotateMainCamera ||
        type === ActionType.MoveMainCamera) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 16,
          }}
        >
          <Space direction='vertical' style={{ width: '48%' }}>
            <span className={styles.label}>Duration (ms):</span>
            <InputNumber
              value={duration}
              onChange={(value) => {
                setDuration(value || 0);
                onChangeNode('duration', value);
              }}
              min={0}
              style={{ width: '100%' }}
            />
          </Space>
        </div>
      )}
    </>
  );
};

export default ActionParams;
